import { SEO } from "@/components/SEO";
import { PageSchema } from "@/components/seo/PageSchema";
import { LastUpdated } from "@/components/seo/LastUpdated";
import { TopCTA } from "@/components/cta/TopCTA";
import { MidPageCTA } from "@/components/cta/MidPageCTA";
import { BottomCTA } from "@/components/cta/BottomCTA";
import { FixSteps } from "@/components/content/FixSteps";
import { DIYvsDoneForYou } from "@/components/content/DIYvsDoneForYou";
import { FAQSection } from "@/components/content/FAQSection";
import { InternalLinks } from "@/components/content/InternalLinks";
import { LeadFormSection } from "@/components/LeadFormSection";

const AddReviewsToWebsite = () => {
  const fixSteps = [
    { step: "Choose which reviews to display", details: "Start with your best 3-5 Google reviews that mention specific services" },
    { step: "Use a review widget or manual display", details: "Widgets auto-update; manual gives more control over what's shown" },
    { step: "Place reviews above the fold on homepage", details: "Visitors should see social proof before they scroll" },
    { step: "Add reviews to service pages", details: "Show service-specific reviews on relevant pages for targeted trust building" },
    { step: "Display star ratings prominently", details: "Visual stars catch attention faster than text testimonials alone" },
    { step: "Include reviewer first name and location", details: "Real names and local areas add authenticity" },
    { step: "Add Review schema markup", details: "Enable star ratings to appear in Google search results" }
  ];

  const widgetOptions = [
    { name: "Elfsight", pros: "Easy setup, auto-syncs with Google", cons: "Free plan shows branding, paid for removal" },
    { name: "EmbedSocial", pros: "Multiple platforms, good customization", cons: "Monthly fee for most features" },
    { name: "Trustindex", pros: "Affordable, multiple review sources", cons: "Can be slow to load" },
    { name: "Manual embedding", pros: "Full control, no external scripts", cons: "Doesn't auto-update, more work" }
  ];

  const faqs = [
    { question: "How do I add Google reviews to my website?", answer: "You can use a review widget (like Elfsight or EmbedSocial) that automatically syncs with your Google Business Profile, or manually copy and paste your best reviews with proper formatting. Widgets auto-update; manual gives you control over which reviews appear." },
    { question: "Do reviews on my website help with conversions?", answer: "Yes, significantly. Websites with displayed reviews convert 270% better than those without. For lawn care specifically, homeowners want proof that you're reliable, show up on time, and do quality work before sharing their contact info." },
    { question: "How many reviews should I display?", answer: "3-5 reviews on your homepage, with the best ones featured prominently. Service pages can show reviews specific to that service. Quality matters more than quantity—a few detailed reviews beat many generic ones." },
    { question: "Should I use a review widget or embed manually?", answer: "Widgets are easier and auto-update but may slow your site and show third-party branding. Manual embedding gives full control and faster loading but requires occasional updates. For small businesses, widgets are usually simpler." },
    { question: "Can I choose which Google reviews to show?", answer: "With manual embedding, you choose exactly which reviews appear. Most widgets show recent reviews automatically but often allow you to hide or feature specific ones. Prioritize detailed reviews that mention specific services." },
    { question: "Where should I put reviews on my website?", answer: "Homepage (above the fold), near quote forms, on service pages (service-specific reviews), and on your About page. Reviews should be visible wherever visitors make decisions about contacting you." },
    { question: "Do I need the reviewer's permission to display their review?", answer: "Google reviews are public, so you can display them on your website. However, use first names and initials (\"John D.\") rather than full names for privacy. Always attribute reviews accurately." },
    { question: "How do I add star ratings to my Google search results?", answer: "Add Review or AggregateRating schema markup to your website. This structured data tells Google about your ratings and can enable star snippets in search results—increasing click-through rates significantly." },
    { question: "Will review widgets slow down my website?", answer: "Some do. Widgets load external scripts that can add 1-3 seconds to page load. Choose lightweight widgets or load them after the main content. Manual embedding has zero speed impact." },
    { question: "How do I get more reviews to display?", answer: "Ask satisfied customers at the end of each job. Send a follow-up text or email with a direct link to your Google review page. Make the process as easy as possible—fewer steps mean more completed reviews." }
  ];

  const links = [
    { to: "/websites/", label: "Website Subscription Pricing" },
    { to: "/lawn-care-website-design/", label: "Lawn Care Website Design" },
    { to: "/growth-add-ons/gbp/", label: "Google Business Profile Optimization" },
    { to: "/website-fixes/lawn-care-website-not-getting-leads/", label: "Website Not Getting Leads" }
  ];

  const CANONICAL = "https://www.leafrushmarketing.com/website-fixes/add-reviews-to-website/";

  return (
    <>
      <SEO
        title="How to Add Google Reviews to Your Lawn Care Website"
        description="Learn how to display Google reviews on your lawn care website to boost trust and conversions. Step-by-step guide with widget options and best practices."
        canonical={CANONICAL}
        ogUrl={CANONICAL}
      />
      <PageSchema
        url={CANONICAL}
        title="How to Add Google Reviews to Your Lawn Care Website"
        description="Learn how to display Google reviews on your lawn care website to boost trust and conversions."
        faqs={faqs}
      />
      <LastUpdated />

      <article className="py-12">
        <div className="container mx-auto px-4 max-w-4xl">
          <header className="mb-8">
            <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
              How to Add Google Reviews to Your Lawn Care Website
            </h1>
            <p className="text-xl text-muted-foreground">
              Display your best reviews and watch conversions climb. Websites with reviews convert 270% better.
            </p>
          </header>

          <TopCTA />

          <section className="my-12">
            <h2 className="text-3xl font-bold text-foreground mb-6">Why Reviews Are Essential for Lawn Care Websites</h2>
            <div className="prose prose-lg max-w-none text-foreground">
              <p>
                Before hiring a lawn care company, homeowners want proof that you're reliable. They want to know you show up on time, do quality work, and communicate well. Your portfolio shows what you can do—reviews prove you actually do it consistently.
              </p>
              <p>
                <strong>Websites with displayed reviews convert 270% better than those without.</strong> That's not a minor improvement—it's the difference between 2 leads per month and 5-6 leads from the same traffic.
              </p>
              <p>
                The key is strategic placement. Reviews should appear wherever visitors make decisions: on your homepage, near quote forms, and on service pages. Each review reinforces trust at the moment it's needed most.
              </p>
            </div>
          </section>

          <MidPageCTA />

          <FixSteps steps={fixSteps} title="7 Steps to Display Reviews on Your Website" />

          <section className="my-12">
            <h2 className="text-3xl font-bold text-foreground mb-6">Review Widget Options</h2>
            <div className="grid md:grid-cols-2 gap-4">
              {widgetOptions.map((option, index) => (
                <div key={index} className="p-4 bg-muted rounded-lg">
                  <h3 className="font-bold text-foreground mb-2">{option.name}</h3>
                  <p className="text-sm text-muted-foreground"><strong>Pros:</strong> {option.pros}</p>
                  <p className="text-sm text-muted-foreground"><strong>Cons:</strong> {option.cons}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="my-12">
            <h2 className="text-3xl font-bold text-foreground mb-6">What Makes a Great Review Display</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="p-6 bg-muted rounded-lg">
                <h3 className="font-bold text-foreground mb-2">✅ Effective Review Display</h3>
                <ul className="space-y-2 text-muted-foreground">
                  <li>• Shows star ratings visually</li>
                  <li>• Features specific, detailed reviews</li>
                  <li>• Includes first name and location</li>
                  <li>• Placed near CTAs and quote forms</li>
                  <li>• Loads quickly without slowing the page</li>
                </ul>
              </div>
              <div className="p-6 bg-muted rounded-lg">
                <h3 className="font-bold text-foreground mb-2">❌ Common Mistakes</h3>
                <ul className="space-y-2 text-muted-foreground">
                  <li>• Generic reviews ("Great service!")</li>
                  <li>• Reviews hidden at bottom of page</li>
                  <li>• No star ratings visible</li>
                  <li>• Anonymous reviewers (no names)</li>
                  <li>• Heavy widgets that slow page load</li>
                </ul>
              </div>
            </div>
          </section>

          <section className="my-12">
            <h2 className="text-3xl font-bold text-foreground mb-6">Sample Review Display Format</h2>
            <div className="bg-muted p-6 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-yellow-500">★★★★★</span>
                <span className="font-semibold text-foreground">5.0</span>
              </div>
              <p className="text-foreground italic mb-2">
                "We've used ABC Lawn Care for three years now. They show up every Wednesday like clockwork, and our lawn has never looked better. The team is professional and always cleans up after themselves. Highly recommend!"
              </p>
              <p className="text-sm text-muted-foreground">— John D., Columbus, OH</p>
            </div>
            <p className="text-muted-foreground mt-4">
              Notice: specific details (3 years, every Wednesday), concrete benefits (lawn looks great, cleanup), and location (Columbus, OH). This is far more persuasive than "Great service!"
            </p>
          </section>

          <DIYvsDoneForYou />

          <FAQSection faqs={faqs} />

          <InternalLinks links={links} title="Related Resources" />

          <BottomCTA />

          <LeadFormSection title="Get a Conversion-Optimized Website" />
        </div>
      </article>
    </>
  );
};

export default AddReviewsToWebsite;
